import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { ChevronRight, Check, Sparkles, Users, Plus, ChevronLeft, Send, Clock, TrendingUp, Contact, Building2, Upload, Trash2 } from 'lucide-react';

import { Button } from '../../Components/UI/button';
import { Input } from '../../Components/UI/input';
import { Label } from '../../Components/UI/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../Components/UI/dialog';
import BusinessInputCustom from '../../Components/Common/BusinessInputCustom';
import BusinessInput from '../../Components/Common/BusinessInput';
import { ContactList } from '../../Components/Common/ContactList';

import { manageConnectionAPI } from '../Connections/ConnectionAPI';
import { manageThreadAPI, saveInquiriesBulk } from '../Threads/ThreadsAPI';
import { verifyMobile, saveUser, setUserTargetAPI } from '../Users/UsersAPI';
import { setGlobalLoading } from '../../Store/Actions';
import { getThreadTypesArr, getAgencyDetails } from '../../Store/Reducers';
import { getAuthData } from '../../Utils/AuthUtils';
import { isLive, isMobileDevice, isContactPickerSupported, getUploadUrl } from '../../Utils/Utilities';
import useIsMobile from '../../Hooks/useIsMobile';
import CustomSearchableDropdown from '../../Components/Common/CustomSearchableDD';
import { segments, states, countries, cityToStateMap } from '../../Components/Enums/DataEnums';
import { updateCompanyProfile } from '../Settings/Profile/ProfileSectionAPI';
import apiService from '../../Services/APIService';
import { cropToCenterSquare } from '../../Utils/ImageCompressor';

const Onboarding = ({ isOpen, setIsOpen }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const authData = getAuthData();
  const currentUserID = authData?.UserID ? String(authData.UserID) : "0";

  const threadTypes = useSelector(getThreadTypesArr);
  const agencyData = useSelector(getAgencyDetails);
  const connections = useSelector(state => state.connectionData.data || []);
  const products = useSelector(state => state.masterData.products || []);
  const threadStages = useSelector(state => state.masterData.threadStages || []);

  const [step, setStep] = useState(0);

  // Theme primary color settings
  const primaryBg = isMobile ? 'bg-sahi-primary-color hover:opacity-90' : 'bg-sahi-primary-color hover:opacity-90';
  const primaryText = 'text-sahi-primary-color';

  // Convert segments data for dropdown
  const segmentOptions = segments.map((segment) => ({
    value: segment.SegmentID,
    label: `${segment.Industry} > ${segment.Segment}`,
    segment: segment.Segment,
    industry: segment.Industry,
    industryID: segment.IndustryID,
    searchableText: `${segment.Industry} ${segment.Segment}`.toLowerCase(),
  }));

  const stateOptions = states
    .filter((state) => state.CountryID === 1)
    .map((state) => ({
      value: state.StateID,
      label: state.State,
      code: state.Code,
    }));

  const countryOptions = countries.map((country) => ({
    value: country.CountryID,
    label: country.Country,
    code: country.CountryCode,
  }));

  const segmentIDFromRedux = useSelector(state => state.masterData.segmentID) || "";

  // STEP 1 STATE (Profile Setup - Promote your Business)
  const [step1ProfileData, setStep1ProfileData] = useState({
    logo: '',
    logoPreview: null,
    segmentID: segmentIDFromRedux || '',
    city: '',
    stateID: 0,
    state: '',
    countryID: 1,
    country: 'India',
  });

  useEffect(() => {
    if (agencyData || segmentIDFromRedux) {
      setStep1ProfileData(prev => ({
        ...prev,
        logo: agencyData?.Logo || agencyData?.LogoPath || agencyData?.logo || "",
        logoPreview: (agencyData?.Logo || agencyData?.LogoPath || agencyData?.logo)
          ? ((agencyData?.Logo || agencyData?.LogoPath || agencyData?.logo).startsWith("http")
            ? (agencyData?.Logo || agencyData?.LogoPath || agencyData?.logo)
            : getUploadUrl(`Uploads/${agencyData?.Logo || agencyData?.LogoPath || agencyData?.logo}`))
          : null,
        segmentID: segmentIDFromRedux || agencyData?.SegmentID || prev.segmentID,
        city: agencyData?.City || prev.city,
        stateID: agencyData?.StateID || prev.stateID,
        state: agencyData?.State || prev.state,
        countryID: agencyData?.CountryID || prev.countryID,
        country: agencyData?.Country || prev.country
      }));
    }
  }, [agencyData, segmentIDFromRedux]);

  const [showLogoActions, setShowLogoActions] = useState(false);
  const logoInputRef = useRef(null);

  // STEP 2 STATE (Turnover, Earning, Target)
  const [step1Data, setStep1Data] = useState({
    turnover: '',
    lastMonthEarning: '',
    target: '',
  });

  // STEP 3 STATE (Invite Coworkers)
  const [wantsToAddUser, setWantsToAddUser] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    contactNo: '',
    designation: '',
    accessLevel: 'basic',
    cc: agencyData?.CC || '91',
    otp: '',
  });
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [otpSentMobile, setOtpSentMobile] = useState('');
  const [invitedUsers, setInvitedUsers] = useState([]);

  // STEP 2 STATE (Top 3 Inquiries)
  const [inquiriesData, setInquiriesData] = useState([
    { name: '', company: '', value: '', mobile: '', email: '' },
    { name: '', company: '', value: '', mobile: '', email: '' },
    { name: '', company: '', value: '', mobile: '', email: '' }
  ]);

  // SCOPE CAROUSEL STATE
  const [activeSlide, setActiveSlide] = useState(0);
  const [hasFinishedScope, setHasFinishedScope] = useState(false);

  useEffect(() => {
    if (activeSlide === 3) {
      setHasFinishedScope(true);
    }
  }, [activeSlide]);

  // Touch Swipe handlers
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 50;

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && activeSlide < 3) {
      setActiveSlide(prev => prev + 1);
    }
    if (isRightSwipe && activeSlide > 0) {
      setActiveSlide(prev => prev - 1);
    }
  };

  // Contact list fallback picker state
  const [contactList, setContactList] = useState([]);
  const [isContactListOpen, setIsContactListOpen] = useState(false);
  const [contactPermissionDenied, setContactPermissionDenied] = useState(false);
  const [activeInquiryIndex, setActiveInquiryIndex] = useState(null);

  // Trigger Native / Fallback Web Contact Picker
  const handleContactPickerClick = async (index) => {
    setActiveInquiryIndex(index);

    if (!isMobileDevice() || !isContactPickerSupported()) {
      toast.error("Contact picker is only supported in the app on mobile devices.");
      return;
    }

    try {
      window.contacts = null;
      setContactPermissionDenied(false);

      window.location.href = "requestcontactpermission://";

      setTimeout(() => {
        window.location.href = "readcontacts://";
      }, 300);

      const maxAttempts = 16;
      let attempts = 0;

      const check = setInterval(() => {
        attempts += 1;

        if (typeof window.contacts !== "undefined" && window.contacts !== null && window.contacts !== "") {
          clearInterval(check);
          let parsedContacts = [];
          try {
            parsedContacts = typeof window.contacts === 'string' ? JSON.parse(window.contacts) : window.contacts;
          } catch (e) {
            console.error("JSON Parse failed for contacts:", e);
          }

          if (parsedContacts && parsedContacts.length > 0) {
            setContactList(parsedContacts);
            setContactPermissionDenied(false);
            setIsContactListOpen(true);
          } else {
            setContactList([]);
          }
          window.contacts = null;
        } else if (attempts >= maxAttempts) {
          clearInterval(check);
          setContactList([]);
        }
      }, 500);
    } catch (error) {
      console.error('Contact picker error:', error);
    }
  };

  const populateInquiryWithContact = (contactData) => {
    if (!contactData || activeInquiryIndex === null) return;

    let rawPhone = contactData.phone;
    if (contactData.phoneNumbers && Array.isArray(contactData.phoneNumbers)) {
      const mobileEntry = contactData.phoneNumbers.find(p =>
        p.label?.toLowerCase().includes('mobile') ||
        p.type?.toLowerCase().includes('mobile') ||
        p.label?.toLowerCase().includes('cell')
      );
      if (mobileEntry) {
        rawPhone = mobileEntry.number || mobileEntry.value;
      }
    }

    let cleanPhone = "";
    if (rawPhone) {
      cleanPhone = rawPhone.replace(/\D/g, '').slice(-10);
    }

    const emailVal = contactData.email && contactData.email !== '-' ? contactData.email : '';

    setInquiriesData(prev => {
      const updated = [...prev];
      updated[activeInquiryIndex] = {
        ...updated[activeInquiryIndex],
        name: contactData.name || '',
        mobile: cleanPhone,
        email: emailVal,
        company: contactData.org && contactData.org !== '-' ? contactData.org : updated[activeInquiryIndex].company
      };
      return updated;
    });

    toast.success("Contact details loaded.");
  };

  // Verify OTP & Add User API Handler
  const handleSendOtp = async () => {
    if (!newUser.name || !newUser.contactNo) {
      toast.error("Please enter the user's name and contact number.");
      return;
    }

    dispatch(setGlobalLoading(true));
    try {
      const response = await verifyMobile(newUser.contactNo, newUser.cc, false);
      if (response.error === false) {
        setIsOtpSent(true);
        setOtpSentMobile(newUser.contactNo);
        toast.success(`OTP sent to ${newUser.name}'s mobile number!`);
      } else {
        toast.error(response.message || "Failed to send OTP. Please try again.");
      }
    } catch (error) {
      toast.error("Error sending OTP: " + error.message);
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  const handleAddUser = async () => {
    if (!newUser.otp) {
      toast.error("Please enter the OTP.");
      return;
    }

    dispatch(setGlobalLoading(true));
    try {
      const isAdmin = newUser.accessLevel === 'admin';
      const rightsPayload = {
        userID: 0,
        changeType: "ADD",
        mobile: newUser.contactNo,
        cc: newUser.cc,
        name: newUser.name,
        email: newUser.email,
        designation: newUser.designation || (isAdmin ? "Administrator" : "Team Member"),
        otp: newUser.otp,
        pin: newUser.otp,
        isAdmin: isAdmin ? 1 : 0,
        fullAccess: isAdmin,
        agnConfig: isAdmin,
        connViewTypeID: isAdmin ? 1 : 4,
        connEntry: 1,
        connEdit: isAdmin ? 1 : 2,
        connDel: isAdmin ? 1 : 0,
        thrViewTypeID: isAdmin ? 1 : 2,
        thrActions: isAdmin ? 1 : 2,
        xnActions: isAdmin ? 1 : 0,
        ctrcActions: isAdmin ? 1 : 0,
        supervisorID: currentUserID,
      };

      const response = await saveUser(rightsPayload);
      if (response.error === false) {
        toast.success(`${newUser.name} added to your team successfully!`);
        setInvitedUsers([...invitedUsers, { name: newUser.name, role: newUser.accessLevel }]);

        // Reset form
        setNewUser({
          name: '',
          email: '',
          contactNo: '',
          designation: '',
          accessLevel: 'basic',
          cc: agencyData?.CC || '91',
          otp: '',
        });
        setIsOtpSent(false);
        setWantsToAddUser(false);
      } else {
        toast.error(response.message || "Failed to add user. Verify the OTP.");
      }
    } catch (error) {
      toast.error("Error adding user: " + error.message);
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  const handleSaveProfileStep = async () => {
    dispatch(setGlobalLoading(true));
    try {
      const selectedSegment = segmentOptions.find(s => s.value === step1ProfileData.segmentID);
      const payload = {
        city: step1ProfileData.city?.trim() || "",
        stateID: step1ProfileData.stateID || 0,
        state: step1ProfileData.state?.trim() || "",
        countryID: step1ProfileData.countryID || 1,
        country: step1ProfileData.country?.trim() || "India",
        segmentID: step1ProfileData.segmentID || "",
        industryID: selectedSegment?.industryID || "",
        businessTypes: "",
        whatDoYouSell: "",
        businessTagline: "",
        description: "",
        googleMapsLink: ""
      };

      const result = await updateCompanyProfile("WEBSTORE", payload);
      if (result.error === false) {
        setStep(2);
      } else {
        toast.error(result.message || "Failed to update profile.");
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred while saving profile.");
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  const handleLogoChange = async (e) => {
    const rawFile = e.target.files?.[0];
    if (!rawFile) return;

    if (!rawFile.type.startsWith("image/")) {
      toast.error("Please select a valid image file (PNG, JPG, WEBP).");
      return;
    }

    if (rawFile.size > 10 * 1024 * 1024) {
      toast.error("Logo image size should be less than 10MB.");
      return;
    }

    dispatch(setGlobalLoading(true));
    try {
      // Automatically crop to maximum possible centered square image
      const file = await cropToCenterSquare(rawFile, 1000, 0.9);

      if (file.size > 2 * 1024 * 1024) {
        toast.error("Logo image size should be less than 2MB.");
        return;
      }

      const uploadResult = await apiService.uploadFile(file, "Profile");
      if (uploadResult?.error) {
        toast.error(uploadResult.message || "Failed to upload logo image.");
        return;
      }

      const relativePath = uploadResult.data?.relativePath || uploadResult.data?.fileName || "";

      const payload = {
        changeType: "AGNLOGO",
        companyName: agencyData?.AgencyName || "",
        stateID: 0,
        taxID: "",
        logo: relativePath,
        banner: ""
      };

      const result = await updateCompanyProfile("AGNLOGO", payload);

      if (result && result.error === false) {
        const fullLogoUrl = getUploadUrl(`Uploads/${relativePath}`);
        setStep1ProfileData((prev) => ({
          ...prev,
          logo: relativePath,
          logoPreview: fullLogoUrl
        }));
        toast.success("Logo updated successfully.");
      } else {
        toast.error(result?.message || "Failed to update logo.");
      }
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error("Failed to upload logo. Please try again.");
    } finally {
      dispatch(setGlobalLoading(false));
      if (e.target) e.target.value = "";
    }
  };

  const handleRemoveLogo = async () => {
    dispatch(setGlobalLoading(true));
    try {
      const payload = {
        changeType: "AGNLOGO",
        companyName: agencyData?.AgencyName || "",
        stateID: 0,
        taxID: "",
        logo: "",
        banner: ""
      };
      const result = await updateCompanyProfile("AGNLOGO", payload);
      if (result && result.error === false) {
        setStep1ProfileData((prev) => ({
          ...prev,
          logo: "",
          logoPreview: null
        }));
        toast.success("Logo removed.");
      } else {
        toast.error(result?.message || "Failed to remove logo.");
      }
    } catch (error) {
      console.error("Error removing logo:", error);
      toast.error("Failed to remove logo.");
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  const handleStep1Submit = async () => {
    dispatch(setGlobalLoading(true));
    try {
      // Resolve stage and product details
      const stages = threadStages?.find(t => t.TID === 2)?.Stages || [];
      const defaultStageID = stages.length > 0 ? stages[0].SID : 1;
      const defaultProduct = products.length > 0 ? (products[0].name || products[0].PNm) : "Onboarding Sale";

      // 1. Process Sales/Turnover for current and/or last month using Connection Entry API and Thread Entry API
      const hasCurrentMonthSales = step1Data.turnover && step1Data.turnover.trim() !== '';
      const hasLastMonthSales = step1Data.lastMonthEarning && step1Data.lastMonthEarning.trim() !== '';

      if (hasCurrentMonthSales || hasLastMonthSales) {
        // Find existing 'Other' connection or create one via manageConnectionAPI
        let otherConnID = 0;
        const existingOtherConn = connections.find(c =>
          c.BizName?.trim().toLowerCase() === 'other' ||
          c.ContactName?.trim().toLowerCase() === 'other' ||
          c.name?.trim().toLowerCase() === 'other'
        );

        if (existingOtherConn) {
          otherConnID = Number(existingOtherConn.ConnID || existingOtherConn.connID || 0);
        } else {
          const otherConnPayload = {
            name: 'Other',
            businessName: 'Other',
            connType: 'C',
            source: '',
            pocUserID: Number(currentUserID)
          };
          const connRes = await manageConnectionAPI(otherConnPayload, 'ADD');
          otherConnID = Number(connRes?.connID || connRes?.connectionData?.ConnID || 0);
        }

        // Call manageThreadAPI for each entered month (1 call if only 1 month, 2 calls if both)
        if (hasCurrentMonthSales) {
          const currentMonthPayload = {
            connID: otherConnID,
            typeID: 2, // Order / Sale
            contactPerson: 'Other',
            product: defaultProduct,
            quantity: '1',
            businessValue: Number(String(step1Data.turnover).replace(/,/g, '')),
            dueDT: format(new Date(), 'yyyy-MM-dd') + 'T00:00:00',
            sinceDT: format(new Date(), 'yyyy-MM-dd') + 'T00:00:00',
            notes: 'Current month turnover entered during onboarding',
            source: '',
            pocUserID: Number(currentUserID),
            stageID: defaultStageID,
            type: 'ORD'
          };
          await manageThreadAPI(currentMonthPayload, 'ADDDONEORD', threadTypes);
        }

        if (hasLastMonthSales) {
          const lastMonthDate = new Date();
          lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);
          const lastMonthPayload = {
            connID: otherConnID,
            typeID: 2, // Order / Sale
            contactPerson: 'Other',
            product: defaultProduct,
            quantity: '1',
            businessValue: Number(String(step1Data.lastMonthEarning).replace(/,/g, '')),
            dueDT: format(lastMonthDate, 'yyyy-MM-dd') + 'T00:00:00',
            sinceDT: format(lastMonthDate, 'yyyy-MM-dd') + 'T00:00:00',
            notes: 'Last month earning entered during onboarding',
            source: '',
            pocUserID: Number(currentUserID),
            stageID: defaultStageID,
            type: 'ORD'
          };
          await manageThreadAPI(lastMonthPayload, 'ADDDONEORD', threadTypes);
        }
      }

      // 2. Update Target Earning
      if (step1Data.target && step1Data.target.trim() !== '') {
        const targetPayload = {
          changeType: 'TGT',
          target: {
            TM: 2, // Amount target
            TA: Number(String(step1Data.target).replace(/,/g, '')),
            TC: 0,
          },
        };
        await setUserTargetAPI(targetPayload);
      }

      // 3. Prepare and save top inquiries in bulk
      const inquiriesParams = [];
      const stagesInq = threadStages?.find(t => t.TID === 1)?.Stages || [];
      const defaultStageIDInq = stagesInq.length > 0 ? stagesInq[0].SID : 1;

      for (const inquiry of inquiriesData) {
        if (inquiry.name && inquiry.name.trim() !== '') {
          inquiriesParams.push({
            BizName: (inquiry.company || inquiry.name).trim(),
            ContactName: inquiry.name.trim(),
            Contacts: '[]',
            Mobile: inquiry.mobile || '',
            CC: inquiry.mobile ? 91 : 0,
            Email: inquiry.email || '',
            POCUserID: Number(currentUserID),
            Source: '',
            Notes: 'Inquiry entered during onboarding',
            City: '',
            StateID: 0,
            State: '',
            CountryID: 1,
            Country: 'India',
            TypeID: 1, // Inquiry
            ConnID: 0,
            Type: 'INQ',
            MainProduct: '',
            MainQty: '1',
            Items: '[]',
            DueDT: format(new Date(), 'yyyy-MM-dd') + 'T00:00:00',
            CurrStage: 'New',
            ExtraFields: '[]',
            Model: '',
            BizProspect: Number(inquiry.value) || 0,
            Reference: '',
            SinceDT: format(new Date(), 'yyyy-MM-dd') + 'T00:00:00',
            Stage: defaultStageIDInq,
            SrcThreadID: '0',
            NextTask: '',
            NextTaskDT: null,
            NextTaskType: '-'
          });
        }
      }

      if (inquiriesParams.length > 0) {
        await saveInquiriesBulk(inquiriesParams, '', 1);
      }

      handleFinishOnboarding();
    } catch (error) {
      toast.error("Failed to complete setup: " + (error.message || "Unknown error"));
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  const handleFinishOnboarding = () => {
    localStorage.setItem("onboarding_completed", "1");
    localStorage.setItem("isLoggedIn", "1");
    localStorage.removeItem("needs_onboarding");
    localStorage.removeItem("SignUp");
    setIsOpen(false);
    if (isMobile) {
      navigate('/growth');
    }
  };

  const ProgressBar = () => {
    if (step === 0) return null; // Hide progress bar on welcome screen
    return (
      <div className="w-full space-y-3 mb-6 text-left">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-zinc-500">Step {step} of 3</span>
          {step !== 3 && (
            <Button
              variant="ghost"
              onClick={handleFinishOnboarding}
              className="h-auto px-3 py-1 text-zinc-500 hover:text-zinc-800 text-sm font-medium rounded transition-colors shadow-none"
            >
              Skip All
            </Button>
          )}
        </div>
        {/* Segmented Progress Bar */}
        <div className="flex gap-2 w-full">
          <div className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${step >= 1 ? 'bg-sahi-primary-color' : 'bg-zinc-200'}`} />
          <div className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${step >= 2 ? 'bg-sahi-primary-color' : 'bg-zinc-200'}`} />
          <div className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${step >= 3 ? 'bg-sahi-primary-color' : 'bg-zinc-200'}`} />
        </div>
      </div>
    );
  };

  const renderContent = () => {
    const today = new Date();
    const currentMonthName = format(today, 'MMM');
    const lastMonthDate = new Date();
    lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);
    const lastMonthName = format(lastMonthDate, 'MMM');

    switch (step) {
      case 0:
        const slidesText = [
          {
            title: <>Welcome to<br /><span className="text-sahi-green">SahiClub!</span></>,
            desc: "Let's get your workspace ready."
          },
          {
            title: <>Smart<br /><span className="text-sahi-green">Management</span></>,
            points: [
              <>Finish <strong className="font-bold">Tasks</strong> Faster</>,
              <>Delegate Reliably to <strong className="font-bold">Team</strong></>,
              <>Improve <strong className="font-bold">Meetings</strong> & <strong className="font-bold">Decisions</strong></>
            ]
          },
          {
            title: <>Smart<br /><span className="text-sahi-green">Sales Growth</span></>,
            points: [
              <>Track Lead <strong className="font-bold">Pipeline</strong></>,
              <>Monitor <strong className="font-bold">Team</strong> Performance</>,
              <>Increase <strong className="font-bold">Repeat</strong> Orders</>
            ]
          },
          {
            title: <>Smart<br /><span className="text-sahi-green">Networking</span></>,
            points: [
              <>Promote on B2B <strong className="font-bold">Community</strong></>,
              <>Get Business <strong className="font-bold">Opportunities</strong></>,
              <>Find Better <strong className="font-bold">Suppliers</strong></>
            ],
            comingSoon: true
          }
        ];
        const currentSlide = slidesText[activeSlide];

        return (
          <div className=" text-left flex flex-col h-full overflow-hidden">
            <style>{`
              @keyframes fadeInUp {
                from {
                  opacity: 0;
                  transform: translateY(12px);
                }
                to {
                  opacity: 1;
                  transform: translateY(0);
                }
              }
              .animate-fade-in-up {
                animation: fadeInUp 400ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
              }
              .animate-slide-text {
                animation: slideText 350ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
              }
              @keyframes slideText {
                from { opacity: 0; transform: translateY(-6px); }
                to { opacity: 1; transform: translateY(0); }
              }
            `}</style>

            {/* Logo Header & Skip Buttons */}
            <div className="flex items-center justify-between mb-12 sm:mb-8 select-none shrink-0">
              <img
                src={isLive() ? "/Web/assets/Images/SahiLogo.png" : "/assets/Images/SahiLogo.png"}
                alt="SahiClub Logo"
                className="h-6 w-auto object-contain"
              />
              <div className="flex items-center gap-1.5">
                {(activeSlide === 1 || activeSlide === 2) && (
                  <Button
                    variant="ghost"
                    onClick={() => setStep(1)}
                    className="h-auto px-2.5 py-1 text-sm text-zinc-400 hover:text-zinc-700 bg-transparent font-medium rounded transition-colors shadow-none"
                  >
                    Skip
                  </Button>
                )}
                {activeSlide === 0 && (
                  <Button
                    variant="ghost"
                    onClick={handleFinishOnboarding}
                    className="h-auto px-2.5 py-1 text-sm text-zinc-500 hover:text-zinc-800 bg-transparent font-medium rounded transition-colors shadow-none"
                  >
                    Skip to Dashboard
                  </Button>
                )}
              </div>
            </div>

            {/* Split layout: side-by-side on desktop (sm:), stacked on mobile */}
            <div className="flex flex-col sm:flex-row flex-1 sm:items-center sm:gap-10 overflow-hidden sm:pb-0 sm:ml-6">

              {/* Left Column (Desktop) / Top Section (Mobile): Title & Description */}
              <div key={activeSlide} className="space-y-2.5 animate-slide-text sm:w-[45%] shrink-0 sm:pr-8">
                <h2 className="text-4xl sm:text-[34px] font-bold text-zinc-900 leading-[1.12] whitespace-pre-line">
                  {currentSlide.title}
                </h2>
                {currentSlide.desc && (
                  <p className="text-[14px] sm:text-sm text-zinc-500 leading-relaxed max-w-[430px]">
                    {currentSlide.desc}
                  </p>
                )}
                {(currentSlide.points || currentSlide.comingSoon) && (
                  <div className="space-y-1">
                    {currentSlide.points && (
                      <ul className="space-y-2.5 max-w-[430px] mt-4">
                        {currentSlide.points.map((pt, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-[14px] sm:text-sm text-zinc-600 leading-normal">
                            <span className="p-0.5 rounded-full bg-sahi-green-light text-sahi-green mt-0.5 shrink-0">
                              <Check className="h-3 w-3 stroke-[3px]" />
                            </span>
                            <span>{pt}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {/* {currentSlide.comingSoon && (
                      <span className="inline-block text-xs font-semibold text-zinc-400 italic ml-7">
                        (coming soon)
                      </span>
                    )} */}
                  </div>
                )}
              </div>

              {/* Right Column (Desktop) / Bottom Section (Mobile): Carousel Image */}
              <div className="flex-1 flex flex-col justify-between py-2 sm:py-0 overflow-hidden mt-6 sm:mt-0 h-full">
                {/* Carousel Slides Track */}
                <div
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  className="relative overflow-hidden flex-1 min-h-[260px] sm:min-h-[340px] flex flex-col justify-center cursor-grab active:cursor-grabbing select-none"
                >
                  <div
                    className="flex transition-transform duration-300 ease-out h-full items-center"
                    style={{ transform: `translateX(-${activeSlide * 25}%)`, width: '400%' }}
                  >
                    {/* Slide 0: Welcome illustration */}
                    <div className="w-1/4 px-1 flex flex-col items-center justify-center text-center shrink-0 h-full">
                      <img
                        src={isLive() ? "/Web/assets/Images/Illustrations/illustration.png" : "/assets/Images/Illustrations/illustration.png"}
                        alt="Welcome Illustration"
                        className="w-72 sm:w-96 h-auto object-contain mx-auto"
                      />
                    </div>

                    {/* Slide 1: Smart Management */}
                    <div className="w-1/4 px-1 shrink-0 flex flex-col justify-center h-full">
                      <img
                        src={isLive() ? "/Web/assets/Images/Onboarding/Tasks.png" : "/assets/Images/Onboarding/Tasks.png"}
                        alt="Smart Management"
                        className="w-80 sm:w-96 h-auto object-contain mx-auto"
                      />
                    </div>

                    {/* Slide 2: Smart Sales Growth */}
                    <div className="w-1/4 px-1 shrink-0 flex flex-col justify-center h-full">
                      <img
                        src={isLive() ? "/Web/assets/Images/Onboarding/Inquiries.png" : "/assets/Images/Onboarding/Inquiries.png"}
                        alt="Smart Sales Growth"
                        className="w-80 sm:w-96 h-auto object-contain mx-auto"
                      />
                    </div>

                    {/* Slide 3: Smart Networking */}
                    <div className="w-1/4 px-1 shrink-0 flex flex-col justify-center h-full">
                      <img
                        src={isLive() ? "/Web/assets/Images/Onboarding/Network.png" : "/assets/Images/Onboarding/Network.png"}
                        alt="Smart Networking"
                        className="w-80 sm:w-96 h-auto object-contain mx-auto"
                      />
                    </div>
                  </div>
                </div>

                {/* Dots and Navigation Arrows */}
                <div className="flex items-center justify-between pt-4 border-t border-zinc-100 mt-2">
                  {activeSlide > 0 ? (
                    <Button
                      variant="ghost"
                      onClick={() => setActiveSlide(prev => prev - 1)}
                      className="h-8 px-2.5 text-xs text-zinc-500 shadow-none hover:bg-zinc-100"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" /> Prev
                    </Button>
                  ) : (
                    <div className="w-[60px]" />
                  )}

                  {/* Dot indicators */}
                  <div className="flex gap-1.5">
                    {[0, 1, 2, 3].map((idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveSlide(idx)}
                        className={`h-2 rounded-full transition-all duration-300 ${activeSlide === idx ? 'w-4 bg-sahi-primary-color' : 'w-2 bg-zinc-200'}`}
                      />
                    ))}
                  </div>

                  {activeSlide < 3 ? (
                    <Button
                      variant="ghost"
                      onClick={() => setActiveSlide(prev => prev + 1)}
                      className="h-8 px-2.5 text-xs text-zinc-500 shadow-none hover:bg-zinc-100"
                    >
                      Next <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  ) : (
                    !isMobile ? (
                      <Button
                        variant="ghost"
                        onClick={() => setStep(1)}
                        className="h-8 px-2.5 text-xs text-zinc-500 shadow-none hover:bg-zinc-100"
                      >
                        Start <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    ) : (
                      <div className="w-[60px]" />
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-4 text-left flex flex-col h-full overflow-hidden">
            <div>
              <span className="text-[11px] font-bold text-sahi-primary-color tracking-wider uppercase">Step 1 of 3</span>
              <h2 className="text-2xl font-bold text-zinc-800 mt-0.5">Promote your Business</h2>
              <p className="text-sm text-zinc-500 mt-1 leading-relaxed">
                Set up your presence on the Sahi B2B Network.
              </p>
            </div>

            <div className="space-y-4 overflow-y-auto flex-1 pr-1 no-scrollbar pb-4" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              <style>{`
                .no-scrollbar::-webkit-scrollbar {
                  display: none;
                }
              `}</style>
              {/* Logo upload field */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Company Logo</Label>
                <div className="flex flex-col items-start gap-3">
                  <div
                    onClick={() => {
                      if (!step1ProfileData.logoPreview) {
                        logoInputRef.current?.click();
                      } else {
                        setShowLogoActions(!showLogoActions);
                      }
                    }}
                    className={`w-40 h-40 rounded-lg border border-dashed flex flex-col items-center justify-center overflow-hidden flex-shrink-0 transition-all duration-300 relative group cursor-pointer ${step1ProfileData.logoPreview
                      ? 'border-zinc-200 bg-zinc-50'
                      : 'border-zinc-200 bg-zinc-50'
                      }`}
                  >
                    {step1ProfileData.logoPreview ? (
                      <>
                        <img src={step1ProfileData.logoPreview} alt="Logo" className="w-full h-full object-contain p-1.5" />

                        {/* Modern Action Overlay */}
                        <div className={`absolute inset-0 bg-black/50 flex items-center justify-center gap-3 transition-opacity duration-300 ${showLogoActions ? 'opacity-100 pointer-events-auto' : 'opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto'
                          }`}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              logoInputRef.current?.click();
                              setShowLogoActions(false);
                            }}
                            className="p-2 bg-white hover:bg-zinc-100 text-zinc-800 rounded-full shadow-md transform hover:scale-110 active:scale-95 transition-all duration-200 cursor-pointer flex items-center justify-center"
                            title="Change Logo"
                          >
                            <Upload className="w-5 h-5 text-[var(--sahi-green)]" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveLogo();
                              setShowLogoActions(false);
                            }}
                            className="p-2 bg-white hover:bg-zinc-100 text-red-650 rounded-full shadow-md transform hover:scale-110 active:scale-95 transition-all duration-200 cursor-pointer flex items-center justify-center"
                            title="Remove Logo"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center p-2 text-center select-none">
                        <Upload className="w-5 h-5 text-[var(--sahi-green,#0b5d1e)] mb-1.5" />
                        <span className="text-[11px] font-bold text-zinc-700">Upload Logo</span>
                        <span className="text-[9px] text-zinc-500 mt-0.5">Max 2MB</span>
                      </div>
                    )}
                  </div>

                  {/* Hidden Input File Element */}
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png, image/jpeg, image/jpg, image/webp"
                    className="hidden"
                    onChange={handleLogoChange}
                  />
                </div>
              </div>

              {/* Industry & Segment */}
              <div className="space-y-1">
                <Label className="text-sm font-medium">Industry & Segment</Label>
                <CustomSearchableDropdown
                  fieldID="segment"
                  placeholder="Select Industry & Segment"
                  options={segmentOptions}
                  value={step1ProfileData.segmentID}
                  onSelect={(val) => setStep1ProfileData(prev => ({ ...prev, segmentID: val }))}
                  className="w-full bg-white h-11 text-sm shadow-xs"
                />
              </div>

              {/* City & State (Side-by-side on desktop, stacked on mobile) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* City */}
                <div className="space-y-1">
                  <Label className="text-sm font-medium">City</Label>
                  <Input
                    type="text"
                    placeholder="e.g. Ahmedabad"
                    value={step1ProfileData.city}
                    onChange={(e) => {
                      const val = e.target.value;
                      let matchedStateID = step1ProfileData.stateID;
                      let matchedStateName = step1ProfileData.state;

                      if (val) {
                        const trimmedCity = val.trim().toLowerCase();
                        const foundCityKey = Object.keys(cityToStateMap).find(key => key.toLowerCase() === trimmedCity);
                        if (foundCityKey) {
                          const stateNameFromMap = cityToStateMap[foundCityKey];
                          const matchedStateObj = states.find(s => s.State.toLowerCase() === stateNameFromMap.toLowerCase());
                          if (matchedStateObj) {
                            matchedStateID = matchedStateObj.StateID;
                            matchedStateName = matchedStateObj.State;
                          }
                        }
                      }

                      setStep1ProfileData(prev => ({
                        ...prev,
                        city: val,
                        stateID: matchedStateID,
                        state: matchedStateName
                      }));
                    }}
                    className="bg-white sahi-input h-11 text-sm shadow-xs"
                  />
                </div>

                {/* State */}
                <div className="space-y-1">
                  <Label className="text-sm font-medium">State</Label>
                  {step1ProfileData.countryID === 1 ? (
                    <CustomSearchableDropdown
                      fieldID="state"
                      placeholder="Select State"
                      options={stateOptions}
                      value={step1ProfileData.stateID}
                      onSelect={(val) => {
                        const matchedState = states.find(s => s.StateID === Number(val));
                        setStep1ProfileData(prev => ({
                          ...prev,
                          stateID: Number(val),
                          state: matchedState ? matchedState.State : ""
                        }));
                      }}
                      className="w-full bg-white h-11 text-sm shadow-xs"
                    />
                  ) : (
                    <Input
                      type="text"
                      placeholder="e.g. State / Region"
                      value={step1ProfileData.state}
                      onChange={(e) => {
                        setStep1ProfileData(prev => ({
                          ...prev,
                          stateID: 0,
                          state: e.target.value
                        }));
                      }}
                      className="bg-white sahi-input h-11 text-sm shadow-xs"
                    />
                  )}
                </div>
              </div>

              {/* Country */}
              <div className="space-y-1">
                <Label className="text-sm font-medium">Country</Label>
                <CustomSearchableDropdown
                  fieldID="country"
                  placeholder="Select Country"
                  options={countryOptions}
                  value={step1ProfileData.countryID}
                  onSelect={(val) => {
                    const matchedCountry = countries.find(c => c.CountryID === Number(val));
                    setStep1ProfileData(prev => ({
                      ...prev,
                      countryID: Number(val),
                      country: matchedCountry ? matchedCountry.Country : "",
                      ...(Number(val) !== 1 ? { stateID: 0, state: "" } : {})
                    }));
                  }}
                  className="w-full bg-white h-11 text-sm shadow-xs"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-5 text-left flex flex-col h-full overflow-hidden">
            <div>
              <span className="text-[11px] font-bold text-sahi-primary-color tracking-wider uppercase">Step 2 of 3</span>
              <h2 className="text-2xl font-bold text-zinc-800 mt-0.5">Set your Numbers</h2>
              <p className="text-sm text-zinc-500 mt-1 leading-relaxed">
                Your sales targets and performance can inspire and align you every time you access the dashboard.
              </p>
            </div>

            <div
              className="space-y-4 overflow-y-auto flex-1 pr-1 no-scrollbar"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              <style>{`
                .no-scrollbar::-webkit-scrollbar {
                  display: none;
                }
              `}</style>
              <div>
                <BusinessInputCustom
                  fieldID="lastMonthEarning"
                  label={`Sales Last Month (${lastMonthName})`}
                  placeholder="e.g. 450000"
                  value={step1Data.lastMonthEarning}
                  onChange={(e) => setStep1Data({ ...step1Data, lastMonthEarning: e.target.value })}
                  required={false}
                  maxDecimalPlaces={0}
                  countryID={step1ProfileData.countryID}
                  color="green"
                />
              </div>

              <div>
                <BusinessInputCustom
                  fieldID="target"
                  label={`Target for This Month (${currentMonthName})`}
                  placeholder="e.g. 600000"
                  value={step1Data.target}
                  onChange={(e) => setStep1Data({ ...step1Data, target: e.target.value })}
                  required={false}
                  maxDecimalPlaces={0}
                  countryID={step1ProfileData.countryID}
                  color="blue"
                />
              </div>

              <div>
                <BusinessInputCustom
                  fieldID="turnover"
                  label={`Sales Already Done (${currentMonthName})`}
                  placeholder="e.g. 500000"
                  value={step1Data.turnover}
                  onChange={(e) => setStep1Data({ ...step1Data, turnover: e.target.value })}
                  required={false}
                  maxDecimalPlaces={0}
                  countryID={step1ProfileData.countryID}
                  color="orange"
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4 text-left flex flex-col h-full overflow-hidden">
            <div>
              <span className="text-[11px] font-bold text-sahi-primary-color tracking-wider uppercase">Step 3 of 3</span>
              <h2 className="text-2xl font-bold text-zinc-800 mt-0.5">Enter Top 3 Inquiries</h2>
              <p className="text-sm text-zinc-500 mt-1 leading-relaxed">
                Let's start with your VIP leads. You can then enter other inquiries easily after getting started.
              </p>
            </div>

            <div
              className="space-y-5 overflow-y-auto scrollbar-no-arrows mt-2 divide-y divide-zinc-200/60 flex-1 no-scrollbar"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {inquiriesData.map((inquiry, idx) => (
                <div key={idx} className={`space-y-2.5 ${idx > 0 ? 'pt-4' : ''}`}>
                  <div className="text-xs font-bold text-sahi-primary-color flex items-center justify-between">
                    <span>Inquiry #{idx + 1}</span>
                  </div>
                  {/* Grid wrapper: Business Name full-width on mobile (row 1), Contact & Expected Value side-by-side (row 2). Side-by-side on desktop. */}
                  <div className="grid grid-cols-[1.1fr_0.9fr] sm:grid-cols-[1.3fr_1fr_0.9fr] gap-3 items-center">
                    {/* Business Name input with picker button */}
                    <div className="col-span-2 sm:col-span-1 order-1 sm:order-2 relative min-w-0">
                      <Input
                        placeholder="Business Name"
                        value={inquiry.company}
                        onChange={(e) => {
                          const val = e.target.value;
                          setInquiriesData(prev => {
                            const updated = [...prev];
                            updated[idx].company = val;
                            return updated;
                          });
                        }}
                        className={`${isMobile ? "pr-10" : ""} bg-white sahi-input h-11 text-sm shadow-xs`}
                      />
                      {isMobile && (
                        <Button
                          variant="ghost"
                          onClick={() => handleContactPickerClick(idx)}
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0 hover:bg-zinc-100 text-zinc-400 shrink-0 shadow-none"
                        >
                          <Contact className="h-4 w-4 text-zinc-400" />
                        </Button>
                      )}
                    </div>

                    {/* Contact Name input */}
                    <div className="col-span-1 sm:col-span-1 order-2 sm:order-1 min-w-0">
                      <Input
                        placeholder="Contact Name"
                        value={inquiry.name}
                        onChange={(e) => {
                          const val = e.target.value;
                          setInquiriesData(prev => {
                            const updated = [...prev];
                            updated[idx].name = val;
                            return updated;
                          });
                        }}
                        className="bg-white sahi-input h-11 text-sm shadow-xs"
                      />
                    </div>

                    {/* Expected Value input */}
                    <div className="col-span-1 sm:col-span-1 order-3 sm:order-3 min-w-0">
                      <BusinessInput
                        fieldID={`inquiry-value-${idx}`}
                        placeholder="Potential"
                        value={inquiry.value}
                        onChange={(e) => {
                          const val = e.target.value;
                          setInquiriesData(prev => {
                            const updated = [...prev];
                            updated[idx].value = val;
                            return updated;
                          });
                        }}
                        required={false}
                        maxDecimalPlaces={0}
                        maxIntegerDigits={10}
                        className="space-y-0"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const renderFooter = () => {
    if (step === 0) {
      if (isMobile) {
        if (activeSlide < 3) return null;
        return (
          <div className="pt-2 mt-0 flex w-full">
            <Button
              variant="default"
              onClick={() => setStep(1)}
              className="h-11 w-full font-semibold text-sm flex items-center justify-center gap-1.5 shadow-none animate-fade-in-up"
            >
              Let's Start <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        );
      }
      return null;
    }

    return (
      <div className="pt-4 mt-0 flex justify-between items-center gap-3">
        {step > 0 ? (
          <Button
            variant="secondary"
            onClick={() => setStep(prev => prev - 1)}
            className="h-11 px-4 font-semibold text-sm flex items-center gap-1.5 shadow-none"
          >
            <ChevronLeft className="h-4 w-4" /> Back
          </Button>
        ) : (
          <div />
        )}

        <div className="flex items-center gap-2">
          {step === 1 && (
            <>
              <Button
                variant="ghost"
                onClick={() => setStep(2)}
                className="h-11 px-3 text-zinc-500 hover:text-zinc-800 text-sm font-medium"
              >
                Skip
              </Button>
              <Button
                variant="default"
                onClick={handleSaveProfileStep}
                className="h-11 px-5 font-semibold text-sm flex items-center gap-1.5 shadow-none"
              >
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </>
          )}

          {step === 2 && (
            <>
              <Button
                variant="ghost"
                onClick={() => setStep(3)}
                className="h-11 px-3 text-zinc-500 hover:text-zinc-800 text-sm font-medium"
              >
                Skip
              </Button>
              <Button
                variant="default"
                onClick={() => setStep(3)}
                className="h-11 px-5 font-semibold text-sm flex items-center gap-1.5 shadow-none"
              >
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </>
          )}

          {step === 3 && (
            <>
              <Button
                variant="ghost"
                onClick={handleFinishOnboarding}
                className="h-11 px-3 text-zinc-500 hover:text-zinc-800 text-sm font-medium"
              >
                Skip
              </Button>
              <Button
                variant="default"
                onClick={async () => {
                  // Validate inquiries: if value or businessName is entered, contactName is required
                  for (let i = 0; i < inquiriesData.length; i++) {
                    const inq = inquiriesData[i];
                    if ((inq.company || inq.value) && !inq.name) {
                      toast.error(`Please enter a Contact Name for Inquiry #${i + 1}`);
                      return;
                    }
                  }
                  await handleStep1Submit();
                }}
                className="h-11 px-5 font-semibold text-sm flex items-center gap-1.5 shadow-none"
              >
                Finish <Check className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </div>
    );
  };

  const getDialogWidth = () => {
    if (step === 0 || step === 3) return 'sm:!max-w-[740px]';
    return 'sm:!max-w-[480px]';
  };

  if (!isOpen) return null;

  if (isMobile) {
    return (
      <div className="fixed inset-0 z-[90] bg-background flex flex-col h-full w-full p-6 overflow-hidden">
        {/* Header */}
        <ProgressBar />

        {/* Form Body */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {renderContent()}
        </div>

        {/* Footer */}
        {renderFooter()}

        {/* Contact List Fallback Picker */}
        <ContactList
          externalOpen={isContactListOpen}
          onOpenChange={setIsContactListOpen}
          contacts={contactList}
          permissionDenied={contactPermissionDenied}
          onContactClick={(contact) => {
            populateInquiryWithContact(contact);
            setIsContactListOpen(false);
          }}
        />
      </div>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      const needsOnboarding = localStorage.getItem("needs_onboarding") === "1";
      if (!open && needsOnboarding) return;
      setIsOpen(open);
    }}>
      <DialogContent
        onEscapeKeyDown={(e) => {
          if (localStorage.getItem("needs_onboarding") === "1") {
            e.preventDefault();
          }
        }}
        onPointerDownOutside={(e) => {
          if (localStorage.getItem("needs_onboarding") === "1") {
            e.preventDefault();
          }
        }}
        className={`w-full ${getDialogWidth()} min-h-[500px] p-6 bg-background border border-zinc-200 shadow-xl rounded-2xl overflow-hidden flex flex-col justify-between transition-all duration-300 ease-in-out`}
      >
        {/* Header */}
        <ProgressBar />

        {/* Form Body */}
        <div className="flex-1 flex flex-col overflow-hidden py-2">
          {renderContent()}
        </div>

        {/* Footer */}
        {renderFooter()}

        {/* Contact List Fallback Picker */}
        <ContactList
          externalOpen={isContactListOpen}
          onOpenChange={setIsContactListOpen}
          contacts={contactList}
          permissionDenied={contactPermissionDenied}
          onContactClick={(contact) => {
            populateInquiryWithContact(contact);
            setIsContactListOpen(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default Onboarding;
